/**
 * BEATSEEK Festival & Events Component
 * Live event discovery with Ticketmaster + EDMtrain integration
 * Features: Search, filters, vibrant cyan/magenta gradient UI
 * Social: I'm Going (with calendar integration), Community Share, Discord Share, Likes, Reactions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, MapPin, Ticket, Camera, Users, Star, Search, Filter, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, RefreshCw, Loader2, Heart, Share2, Menu } from 'lucide-react';
import axios from 'axios';

// Import extracted sub-components
import { EventCard, MyEventCard, PaginationControls } from './FestivalEvents/index';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FestivalEvents = ({ isOpen, onClose, onDiscordShare, initialTab }) => {
  const [selectedTab, setSelectedTab] = useState(initialTab || 'upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle responsive state
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Reset to initialTab when modal opens
  useEffect(() => {
    if (isOpen && initialTab) {
      setSelectedTab(initialTab);
    }
  }, [isOpen, initialTab]);
  
  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Attendance state
  const [attendanceData, setAttendanceData] = useState({}); // { eventId: { is_going, beatseekers_attending, friends_attending } }
  const [loadingAttendance, setLoadingAttendance] = useState({});
  
  // Social interactions state
  const [likedEvents, setLikedEvents] = useState(new Set());
  const [eventLikes, setEventLikes] = useState({});
  const [showReactions, setShowReactions] = useState(null);
  
  // Reaction emojis
  const reactionEmojis = ['🔥', '❤️', '🎵', '🙌', '💃', '🎉'];
  
  // Event Types
  const eventTypes = [
    { id: 'all', name: 'All Events', icon: '✨' },
    { id: 'festival', name: 'Festival', icon: '🎪' },
    { id: 'club', name: 'Club Night', icon: '🎵' },
    { id: 'warehouse', name: 'Warehouse Party', icon: '🏭' },
    { id: 'pool-party', name: 'Pool Party', icon: '🏊' },
    { id: 'boat-party', name: 'Boat Party', icon: '🚢' },
    { id: 'rooftop', name: 'Rooftop', icon: '🌆' },
  ];

  // Genre filter state - now supports multiple selections
  const [selectedGenres, setSelectedGenres] = useState([]);
  
  // Source filter state
  const [selectedSource, setSelectedSource] = useState('all');
  
  // Month filter state
  const [selectedMonth, setSelectedMonth] = useState('all');
  
  // Generate next 12 months for filter
  const getMonthOptions = () => {
    const months = [{ value: 'all', label: 'All Months' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };
  const monthOptions = getMonthOptions();
  
  // Event Sources - for API filtering (ordered: EDMtrain, Skiddle, Ticketmaster)
  // Note: Eventbrite API deprecated public search - pending special access request
  const eventSources = [
    { id: 'all', name: 'All Sources', icon: '🌐' },
    { id: 'edmtrain', name: 'Edmtrain', icon: '🎵' },
    { id: 'skiddle', name: 'Skiddle (UK)', icon: '🎭' },
    { id: 'ticketmaster', name: 'Ticketmaster', icon: '🎫' },
    // { id: 'eventbrite', name: 'Eventbrite', icon: '🎟️' }, // Disabled - awaiting API access
  ];
  
  // Toggle genre selection
  const toggleGenre = (genreId) => {
    if (genreId === 'all') {
      // Clicking "All" clears all selections
      setSelectedGenres([]);
    } else {
      setSelectedGenres(prev => {
        if (prev.includes(genreId)) {
          // Remove if already selected
          return prev.filter(g => g !== genreId);
        } else {
          // Add to selection
          return [...prev, genreId];
        }
      });
    }
  };
  
  // EDM Genres - Alphabetically sorted with Melodic and Progressive added
  const edmGenres = [
    { id: 'all', name: 'All Genres', icon: '🎵' },
    { id: 'bass', name: 'Bass Music', icon: '🔊' },
    { id: 'disco', name: 'Disco / Nu-Disco', icon: '🕺' },
    { id: 'drum-and-bass', name: 'Drum & Bass', icon: '🥁' },
    { id: 'dubstep', name: 'Dubstep', icon: '💥' },
    { id: 'edm', name: 'EDM / Mainstage', icon: '🎆' },
    { id: 'hardstyle', name: 'Hardstyle', icon: '💪' },
    { id: 'house', name: 'House', icon: '🏠' },
    { id: 'melodic', name: 'Melodic', icon: '🎹' },
    { id: 'progressive', name: 'Progressive', icon: '🌊' },
    { id: 'tech-house', name: 'Tech House', icon: '⚡' },
    { id: 'techno', name: 'Techno', icon: '🤖' },
    { id: 'trance', name: 'Trance', icon: '🌀' },
  ];

  // Mock photos for community tab
  const mockPhotos = [
    { id: 1, event: "Ultra Music Festival 2024", user: "DJ_Mike23", caption: "Incredible set by Carl Cox! 🔥", likes: 47, date: "2024-03-30" },
    { id: 2, event: "Awakenings Festival 2024", user: "TechnoLover88", caption: "Nina Kraviz absolutely killed it! ⚡", likes: 32, date: "2024-06-29" },
    { id: 3, event: "Tomorrowland 2024", user: "FestivalQueen", caption: "The mainstage production was out of this world! 🌟", likes: 78, date: "2024-07-21" },
    { id: 4, event: "Movement Detroit 2024", user: "DetroitTechno", caption: "Underground vibes at Hart Plaza! 🏙️", likes: 41, date: "2024-05-27" }
  ];

  // Fetch events from API
  const fetchEvents = useCallback(async (page = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/events/search`, {
        keyword: searchQuery || '',
        location: locationQuery || '',
        event_type: selectedEventType !== 'all' ? selectedEventType : null,
        genre: selectedGenres.length > 0 ? selectedGenres.join(',') : null,
        source: selectedSource !== 'all' ? selectedSource : null,
        month: selectedMonth !== 'all' ? selectedMonth : null,
        page: page,
        page_size: 20
      });
      
      if (response.data.success) {
        const newEvents = response.data.events || [];
        setEvents(newEvents);
        
        // Determine if there are more events to load
        setHasMoreEvents(newEvents.length >= 20);
        setCurrentPage(page);
        
        // Fetch attendance data for all events
        const validEvents = newEvents.filter(e => e && e.id);
        if (validEvents.length > 0) {
          fetchBatchAttendance(validEvents.map(e => e.id));
        }
      } else {
        setError('Failed to fetch events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Unable to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, locationQuery, selectedEventType, selectedGenres, selectedSource, selectedMonth]);
  
  // Go to previous page
  const goToPreviousPage = () => {
    if (currentPage > 0) {
      fetchEvents(currentPage - 1);
    }
  };
  
  // Go to next page
  const goToNextPage = () => {
    if (hasMoreEvents) {
      fetchEvents(currentPage + 1);
    }
  };

  // Fetch attendance data for multiple events
  const fetchBatchAttendance = async (eventIds) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token || !eventIds || eventIds.length === 0) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events/attendance/batch`, {
        params: { event_ids: eventIds.filter(id => id).join(',') },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success && response.data.events) {
        setAttendanceData(response.data.events);
      }
      
      // Also fetch likes
      const likesResponse = await axios.get(`${BACKEND_URL}/api/events/likes/batch`, {
        params: { event_ids: eventIds.filter(id => id).join(',') },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (likesResponse.data && likesResponse.data.success && likesResponse.data.events) {
        const likedSet = new Set();
        const likesCount = {};
        Object.entries(likesResponse.data.events).forEach(([eventId, data]) => {
          if (data.is_liked) likedSet.add(eventId);
          likesCount[eventId] = data.total_likes;
        });
        setLikedEvents(likedSet);
        setEventLikes(likesCount);
      }
    } catch (err) {
      console.error('Error fetching attendance/likes:', err);
      // Don't break the UI, just leave data empty
    }
  };

  // Initial fetch
  useEffect(() => {
    if (isOpen && selectedTab === 'upcoming') {
      fetchEvents(0);
    }
  }, [isOpen, selectedTab, fetchEvents]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents(0);
  };

  // Format date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr) return 'Date TBA';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Handle I'm Going toggle
  const handleToggleGoing = async (event) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to mark events as "I\'m Going"');
      return;
    }
    
    setLoadingAttendance(prev => ({ ...prev, [event.id]: true }));
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/events/attendance/toggle`, {
        event_id: event.id,
        event_name: event.name,
        event_date: event.start_local || event.start_datetime,  // Use local date to avoid timezone issues
        event_location: event.location,
        event_venue: event.venue?.name,
        event_image_url: event.image_url,
        event_url: event.url,
        event_source: event.source,
        event_price: event.price_range?.display
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAttendanceData(prev => ({
          ...prev,
          [event.id]: {
            is_going: response.data.is_going,
            beatseekers_attending: response.data.beatseekers_attending,
            friends_attending: response.data.friends_attending
          }
        }));
        
        // Show success message
        if (response.data.is_going) {
          alert(response.data.message);
        }
      }
    } catch (err) {
      console.error('Error toggling attendance:', err);
      alert('Failed to update attendance. Please try again.');
    } finally {
      setLoadingAttendance(prev => ({ ...prev, [event.id]: false }));
    }
  };

  // Handle removing event from My Events (uses the same toggle endpoint)
  const handleRemoveFromMyEvents = async (event) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in');
      return;
    }
    
    // Confirm removal
    if (!window.confirm(`Remove "${event.event_name}" from your events?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/events/attendance/toggle`, {
        event_id: event.event_id || event.id,
        event_name: event.event_name,
        event_date: event.event_date,
        event_location: event.event_location,
        event_venue: event.event_venue,
        event_image_url: event.event_image_url,
        event_url: event.event_url,
        event_source: event.event_source
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && !response.data.is_going) {
        // Remove from myEvents list
        setMyEvents(prev => prev.filter(e => (e.event_id || e.id) !== (event.event_id || event.id)));
        // Also update attendance data for upcoming events tab
        setAttendanceData(prev => ({
          ...prev,
          [event.event_id || event.id]: {
            is_going: false,
            beatseekers_attending: response.data.beatseekers_attending || 0,
            friends_attending: response.data.friends_attending || 0
          }
        }));
      }
    } catch (err) {
      console.error('Error removing event:', err);
      alert('Failed to remove event. Please try again.');
    }
  };

  // Handle like toggle - now persisted to backend
  const handleLike = async (eventId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please log in to like events');
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/events/likes/toggle`, null, {
        params: { event_id: eventId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setLikedEvents(prev => {
          const newSet = new Set(prev);
          if (response.data.is_liked) {
            newSet.add(eventId);
          } else {
            newSet.delete(eventId);
          }
          return newSet;
        });
        setEventLikes(p => ({ ...p, [eventId]: response.data.total_likes }));
      }
    } catch (err) {
      console.error('Error liking event:', err);
    }
  };

  // Handle Community Share
  const handleCommunityShare = (event) => {
    alert(`Share "${event.name}" to community feed coming soon!`);
  };

  // Handle Discord Share
  const handleDiscordShare = (event) => {
    const discordToken = localStorage.getItem('discord_access_token');
    if (!discordToken) {
      alert('Please connect your Discord account!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
      return;
    }
    
    if (onDiscordShare) {
      onDiscordShare({
        title: event.name,
        artist: event.organizer?.name || 'Various Artists',
        artwork: event.image_url,
        genre: event.category?.name || 'EDM',
        location: event.location,
        date: formatEventDate(event.start_datetime),
        url: event.url,
        type: 'event'
      });
    }
  };

  // Handle reaction
  const handleReaction = (eventId, emoji) => {
    console.log(`Reacted with ${emoji} to event ${eventId}`);
    setShowReactions(null);
  };

  const renderUpcomingEvents = () => (
    <div className="space-y-4">
      {/* Search & Filters Bar */}
      <form onSubmit={handleSearch} className="space-y-3">
        {/* Mobile: Stack vertically, Desktop: Horizontal */}
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2`}>
          {/* Search Input */}
          <div className={`${isMobile ? 'w-full' : 'flex-1'} relative`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder={isMobile ? "Search events..." : "Search events, artists, festivals..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 ${isMobile ? 'py-2.5 text-sm' : 'py-3'} bg-gray-800/60 border border-cyan-500/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition-all`}
            />
          </div>
          
          {/* Location Input - Full width on mobile */}
          <div className={`${isMobile ? 'w-full' : 'w-48'} relative`}>
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Zip / City / Region"
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
              className={`w-full pl-10 pr-4 ${isMobile ? 'py-2.5 text-sm' : 'py-3'} bg-gray-800/60 border border-pink-500/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-400/50 transition-all`}
            />
          </div>
          
          {/* Dropdowns Row - Side by side on mobile */}
          <div className={`flex ${isMobile ? 'w-full' : ''} gap-2`}>
            {/* Source Dropdown */}
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className={`${isMobile ? 'flex-1 py-2.5 px-3 text-sm' : 'px-4 py-3'} bg-gray-800/60 border border-pink-500/30 rounded-xl text-white focus:outline-none focus:border-pink-400 cursor-pointer`}
            >
              {eventSources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.icon} {isMobile ? source.name.split(' ')[0] : source.name}
                </option>
              ))}
            </select>
            
            {/* Month Filter Dropdown */}
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className={`${isMobile ? 'flex-1 py-2.5 px-3 text-sm' : 'px-4 py-3'} bg-gray-800/60 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:border-cyan-400 cursor-pointer`}
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  📅 {isMobile ? month.label.split(' ')[0] : month.label}
                </option>
              ))}
            </select>
            
            {/* Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} rounded-xl border transition-all flex items-center gap-1 ${
                showFilters 
                  ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300' 
                  : 'bg-gray-800/60 border-gray-600 text-gray-300 hover:border-gray-500'
              }`}
            >
              <Filter size={isMobile ? 16 : 18} />
              {!isMobile && <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />}
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className={`${isMobile ? 'p-3' : 'p-4'} bg-gray-800/40 rounded-xl border border-gray-700/50 space-y-3`}>
            {/* Event Type Filter */}
            <div className="flex flex-wrap gap-2">
              <span className={`text-gray-400 ${isMobile ? 'text-xs w-full mb-1' : 'text-sm mr-2 self-center'}`}>Event Type:</span>
              {eventTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setSelectedEventType(type.id)}
                  className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-lg font-medium transition-all ${
                    selectedEventType === type.id
                      ? 'bg-gradient-to-r from-cyan-500 to-pink-500 text-white'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                  }`}
                >
                  {type.icon} {isMobile && type.id !== 'all' ? '' : type.name}
                </button>
              ))}
            </div>
            
            {/* Genre Filter */}
            <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-x-1 gap-y-2'} items-center`}>
              <span className={`text-gray-400 ${isMobile ? 'text-xs w-full mb-1' : 'text-sm mr-2'}`}>Genre:</span>
              {edmGenres.map((genre, index) => (
                <React.Fragment key={genre.id}>
                  <span
                    onClick={() => toggleGenre(genre.id)}
                    className={`${isMobile ? 'text-xs' : 'text-sm'} cursor-pointer transition-all px-1 ${
                      genre.id === 'all' 
                        ? (selectedGenres.length === 0 ? 'text-pink-400 font-semibold' : 'text-gray-400 hover:text-white')
                        : (selectedGenres.includes(genre.id) ? 'text-pink-400 font-semibold' : 'text-gray-400 hover:text-white')
                    }`}
                  >
                    {genre.icon} {isMobile ? '' : genre.name}
                  </span>
                  {index < edmGenres.length - 1 && !isMobile && (
                    <span className="text-gray-600 mx-1">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </form>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-400">
          {loading ? 'Searching...' : `${events.length} events found (Page ${currentPage + 1})`}
        </span>
        <button
          onClick={() => fetchEvents(0)}
          disabled={loading}
          className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-center">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="animate-spin text-cyan-400 mb-4" size={40} />
          <p className="text-gray-400">Searching for events...</p>
        </div>
      )}

      {/* Events Grid - Using EventCard component */}
      {!loading && events.length > 0 && (
        <div className="grid gap-5 pr-2">
          {events.map((event) => {
            const attendance = (attendanceData && attendanceData[event.id]) || { is_going: false, beatseekers_attending: 0, friends_attending: 0 };
            const isLoadingThis = loadingAttendance && loadingAttendance[event.id];
            
            return (
              <EventCard
                key={event.id}
                event={event}
                attendance={attendance}
                isLoadingAttendance={isLoadingThis}
                isLiked={likedEvents.has(event.id)}
                likeCount={eventLikes[event.id] || 0}
                showReactions={showReactions === event.id}
                reactionEmojis={reactionEmojis}
                onToggleGoing={handleToggleGoing}
                onLike={handleLike}
                onReaction={handleReaction}
                onToggleReactions={(id) => setShowReactions(showReactions === id ? null : id)}
                onCommunityShare={handleCommunityShare}
                onDiscordShare={handleDiscordShare}
                onViewInCalendar={(evt) => {
                  onClose();
                  const eventDate = evt.start_local || evt.start_datetime;
                  window.dispatchEvent(new CustomEvent('navigateToGigHub', { 
                    detail: { tab: 'calendar', eventDate: eventDate }
                  }));
                }}
                formatEventDate={formatEventDate}
                isMobile={isMobile}
              />
            );
          })}
        </div>
      )}
      
      {/* Pagination Controls - Using PaginationControls component */}
      {!loading && events.length > 0 && (
        <PaginationControls
          currentPage={currentPage}
          hasMoreEvents={hasMoreEvents}
          loading={loading}
          onPreviousPage={goToPreviousPage}
          onNextPage={goToNextPage}
        />
      )}

      {/* Empty State */}
      {!loading && events.length === 0 && !error && (
        <div className={`text-center ${isMobile ? 'py-8' : 'py-16'}`}>
          <div className={`${isMobile ? 'w-16 h-16' : 'w-24 h-24'} bg-gradient-to-br from-cyan-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Calendar className="text-cyan-400" size={isMobile ? 32 : 48} />
          </div>
          <h3 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-white mb-3`}>No Festivals or Events Currently</h3>
          <p className={`text-gray-400 mb-4 ${isMobile ? 'text-sm px-4' : 'max-w-md mx-auto'}`}>
            We're working on bringing you the best EDM events. Check back soon or try a different search!
          </p>
          <button 
            onClick={() => { setSearchQuery(''); setLocationQuery(''); setSelectedEventType('all'); setSelectedGenres([]); }}
            className={`${isMobile ? 'px-4 py-2 text-sm' : 'px-6 py-3'} bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-600/50 transition-all`}
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );

  const renderCommunityPhotos = () => (
    <div className="grid grid-cols-2 gap-6">
      {mockPhotos.map((photo) => (
        <div key={photo.id} className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden hover:border-pink-500/30 transition-all">
          <div className="relative">
            <div className="h-64 bg-gradient-to-r from-pink-600 to-purple-600 flex items-center justify-center">
              <Camera className="text-white" size={48} />
            </div>
            <div className="absolute top-3 left-3 bg-black/60 text-white px-2 py-1 rounded text-xs">
              {photo.event}
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{photo.user.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-gray-200 text-sm font-medium">{photo.user}</p>
                  <p className="text-gray-400 text-xs">{photo.date}</p>
                </div>
              </div>
              <div className="flex items-center text-pink-400">
                <Star className="mr-1" size={14} />
                <span className="text-sm">{photo.likes}</span>
              </div>
            </div>
            <p className="text-gray-300 text-sm">{photo.caption}</p>
          </div>
        </div>
      ))}
    </div>
  );

  // State for My Events tab
  const [myEvents, setMyEvents] = useState([]);
  const [loadingMyEvents, setLoadingMyEvents] = useState(false);

  // Fetch user's attending events
  const fetchMyEvents = useCallback(async () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    
    setLoadingMyEvents(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events/my-attending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success) {
        setMyEvents(response.data.events || []);
      }
    } catch (err) {
      console.error('Error fetching my events:', err);
    } finally {
      setLoadingMyEvents(false);
    }
  }, []);

  // Fetch my events when tab changes
  useEffect(() => {
    if (isOpen && selectedTab === 'my-events') {
      fetchMyEvents();
    }
  }, [isOpen, selectedTab, fetchMyEvents]);

  const renderMyEvents = () => {
    if (loadingMyEvents) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="animate-spin text-cyan-400 mb-4" size={40} />
          <p className="text-gray-400">Loading your events...</p>
        </div>
      );
    }
    
    if (myEvents.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="text-cyan-400" size={40} />
          </div>
          <h3 className="text-xl font-bold text-white mb-4">No Events Yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Mark events as "I'm Going" to keep track of your festival adventures and connect with other Beatseekers.
          </p>
          <button 
            onClick={() => setSelectedTab('upcoming')}
            className="bg-gradient-to-r from-cyan-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:from-cyan-400 hover:to-pink-400 transition-all"
          >
            Discover Events
          </button>
        </div>
      );
    }
    
    return (
      <div className="grid gap-5 pr-2">
        {myEvents.map((event) => {
          const eventDate = event.event_date ? new Date(event.event_date) : null;
          const formattedDate = eventDate ? eventDate.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }) : 'Date TBA';
          
          return (
            <MyEventCard
              key={event.id || event.event_id}
              event={event}
              formattedDate={formattedDate}
              onViewInCalendar={() => {
                onClose();
                window.dispatchEvent(new CustomEvent('navigateToGigHub', { 
                  detail: { tab: 'calendar', eventDate: event.event_date }
                }));
              }}
              onRemove={() => handleRemoveFromMyEvents(event)}
              onDiscordShare={onDiscordShare}
            />
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black/80 flex ${isMobile ? 'items-start' : 'items-center'} justify-center z-50 ${isMobile ? 'p-0' : 'p-4'}`}>
      {/* Animated Gradient Border Wrapper */}
      <div 
        className="festival-modal-wrapper relative" 
        style={{ 
          padding: isMobile ? '0' : '3px', 
          borderRadius: isMobile ? '0' : '20px',
          width: isMobile ? '100%' : '95vw',
          maxWidth: '1280px',
          height: isMobile ? '100%' : 'auto'
        }}
      >
        {!isMobile && (
          <div 
            className="absolute inset-0 rounded-[20px] bg-gradient-to-r from-cyan-500 via-pink-500 via-purple-500 to-cyan-500"
            style={{
              backgroundSize: '300% 100%',
              animation: 'gradientSlide 4s ease-in-out infinite',
              zIndex: 0
            }}
          />
        )}
        
        {/* Main Modal */}
        <div 
          className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 overflow-hidden flex flex-col" 
          style={{ 
            zIndex: 1,
            width: '100%',
            height: isMobile ? '100%' : '85vh',
            borderRadius: isMobile ? '0' : '18px'
          }}
        >
          {/* Header */}
          <div className={`relative bg-gradient-to-r from-cyan-600 via-pink-600 to-purple-600 ${isMobile ? 'p-3' : 'p-5'} border-b border-white/10`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`${isMobile ? 'w-9 h-9' : 'w-12 h-12'} bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg`}>
                  <Calendar className="text-white" size={isMobile ? 18 : 24} />
                </div>
                <div>
                  <h2 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-white`}>Festivals & Events</h2>
                  {!isMobile && <p className="text-cyan-100 text-sm">Discover EDM events worldwide</p>}
                </div>
              </div>
              
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <X className="text-white" size={isMobile ? 20 : 24} />
              </button>
            </div>
            
            {/* Tabs */}
            <div className={`flex ${isMobile ? 'gap-1 overflow-x-auto scrollbar-none' : 'space-x-1'} mt-3 bg-black/20 rounded-xl p-1 ${isMobile ? 'w-full' : 'w-fit'}`}>
              {[
                { id: 'upcoming', label: isMobile ? 'Events' : 'Upcoming Events', icon: Calendar },
                { id: 'photos', label: isMobile ? 'Photos' : 'Community Photos', icon: Camera },
                { id: 'my-events', label: isMobile ? 'My Events' : 'My Events', icon: Star }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center ${isMobile ? 'gap-1 px-3 py-2 text-xs flex-1 justify-center' : 'space-x-2 px-4 py-2'} rounded-lg font-medium transition-all whitespace-nowrap ${
                    selectedTab === tab.id
                      ? 'bg-white text-gray-900'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <tab.icon size={isMobile ? 14 : 16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Content - with padding for card lift */}
          <div className={`flex-1 overflow-y-auto overflow-x-hidden ${isMobile ? 'p-3 pb-24' : 'p-8'} custom-scrollbar`}>
            {selectedTab === 'upcoming' && renderUpcomingEvents()}
            {selectedTab === 'photos' && renderCommunityPhotos()}
            {selectedTab === 'my-events' && renderMyEvents()}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes gradientSlide {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #06b6d4, #ec4899);
          border-radius: 3px;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default FestivalEvents;
